import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'environments/environment';
import { BehaviorSubject, EMPTY, Observable, Subject, throwError } from 'rxjs';
import { catchError, take, takeUntil, tap } from 'rxjs/operators';
import { SocketService } from './socket.service';
import {
    Conversation,
    ConversationContact,
    ConversationMessage,
    ConversationMessageType,
} from 'app/models/conversation.model';
import { IPagination } from 'app/shared/interfaces/pagination.dto';
import { ApiService } from 'app/shared/apis/api.service';

export interface SendMessageContext {
    user_id: number;
    user_name: string;
}

type PendingPayload =
    | { kind: 'text'; content: string }
    | {
          kind: 'media';
          file: File;
          duration_seconds?: number;
          preview?: { type: ConversationMessageType; file_url?: string; file_name?: string };
      };

@Injectable({
    providedIn: 'root',
})
export class ConversationService implements OnDestroy {
    private readonly base = `${environment.apiUrl}/conversation`;
    private destroy$ = new Subject<void>();
    private _messages = new BehaviorSubject<ConversationMessage[]>([]);
    private _conversations = new BehaviorSubject<Conversation[]>([]);
    private joinedConversationId: number | null = null;
    private tempCounter = 0;
    private messageCache = new Map<number, ConversationMessage[]>();
    private pendingPayloads = new Map<string, PendingPayload>();

    currentConversationId: number | null = null;
    private currentUserId: number | null = null;
    private chatNotifyAudioCtx: AudioContext | null = null;
    private lastChatNotifyAt = 0;
    /** Evita processar 2x o mesmo emit (room conversa + room usuário). */
    private recentlySeenMessageIds = new Set<number>();

    constructor(
        private readonly http: HttpClient,
        private readonly socket: SocketService,
    ) {
        ApiService.userCurrent.pipe(take(1)).subscribe((user) => {
            this.currentUserId = user?.user_id ?? null;
        });
        this.socket
            .fromEvent('new_conversation_message')
            .pipe(takeUntil(this.destroy$))
            .subscribe((msg: ConversationMessage) => {
                if (msg?.conversation_id == null) return;
                this.ingestServerMessage(msg);
            });
    }

    setCurrentUserId(userId: number | null): void {
        this.currentUserId = userId;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.chatNotifyAudioCtx) {
            void this.chatNotifyAudioCtx.close().catch(() => undefined);
            this.chatNotifyAudioCtx = null;
        }
    }

    get messages$(): Observable<ConversationMessage[]> {
        return this._messages.asObservable();
    }

    get conversations$(): Observable<Conversation[]> {
        return this._conversations.asObservable();
    }

    list(companyId?: number): Observable<Conversation[]> {
        let params = new HttpParams();
        if (companyId != null) params = params.set('company_id', String(companyId));
        return this.http.get<Conversation[]>(this.base, { params }).pipe(
            tap((items) => this._conversations.next(items || [])),
        );
    }

    listContacts(companyId: number): Observable<ConversationContact[]> {
        const params = new HttpParams().set('company_id', String(companyId));
        return this.http.get<ConversationContact[]>(`${this.base}/contacts`, { params });
    }

    getByService(serviceId: number): Observable<Conversation | null> {
        return this.http.get<Conversation | null>(`${this.base}/by-service/${serviceId}`);
    }

    createDirect(companyId: number, targetUserId: number): Observable<Conversation> {
        return this.http.post<Conversation>(`${this.base}/direct`, {
            company_id: companyId,
            target_user_id: targetUserId,
        });
    }

    createGroup(companyId: number, name: string, participantIds: number[]): Observable<Conversation> {
        return this.http.post<Conversation>(`${this.base}/group`, {
            company_id: companyId,
            name,
            participant_ids: participantIds,
        }).pipe(
            tap((conv) => this.upsertConversation(conv)),
        );
    }

    updateGroup(
        conversationId: number,
        payload: { name?: string; participant_ids?: number[] },
    ): Observable<Conversation> {
        return this.http
            .patch<Conversation>(`${this.base}/${conversationId}`, payload)
            .pipe(tap((conv) => this.upsertConversation(conv)));
    }

    deleteConversation(conversationId: number): Observable<{ ok: boolean }> {
        return this.http.delete<{ ok: boolean }>(`${this.base}/${conversationId}`).pipe(
            tap(() => {
                const next = this._conversations.value.filter((c) => c.conversation_id !== conversationId);
                this._conversations.next(next);
                this.messageCache.delete(conversationId);
                if (this.currentConversationId === conversationId) {
                    this.leaveConversation();
                }
            }),
        );
    }

    startCallRoom(conversationId: number): Observable<Conversation> {
        return this.http.post<Conversation>(`${this.base}/${conversationId}/call-room`, {});
    }

    getOne(conversationId: number): Observable<Conversation> {
        return this.http.get<Conversation>(`${this.base}/${conversationId}`);
    }

    loadMessages(conversationId: number, page = 1, limit: number | 'all' = 50): Observable<IPagination<ConversationMessage>> {
        const cached = this.messageCache.get(conversationId);
        if (cached?.length && this.currentConversationId === conversationId) {
            this._messages.next(cached);
        }

        let params = new HttpParams().set('page', String(page)).set('limit', String(limit));
        return this.http
            .get<IPagination<ConversationMessage>>(`${this.base}/${conversationId}/messages`, { params })
            .pipe(
                tap((res) => {
                    const items = [...(res.items || [])].reverse();
                    const pending =
                        this.currentConversationId === conversationId
                            ? this._messages.value.filter((m) => m.optimistic || m.failed)
                            : [];
                    const merged = this.mergeWithPending(items, pending);
                    this.messageCache.set(conversationId, merged);
                    if (this.currentConversationId === conversationId) {
                        this._messages.next(merged);
                    }
                }),
            );
    }

    sendText(conversationId: number, content: string, ctx: SendMessageContext): Observable<ConversationMessage> {
        const tempId = this.nextTempId();
        const optimistic = this.buildOptimisticMessage({
            tempId,
            conversationId,
            ctx,
            type: 'TEXT',
            content,
        });
        this.pendingPayloads.set(tempId, { kind: 'text', content });
        this.pushOptimistic(optimistic);

        return this.postText(conversationId, content, tempId);
    }

    sendMedia(
        conversationId: number,
        file: File,
        ctx: SendMessageContext,
        preview?: { type: ConversationMessageType; file_url?: string; file_name?: string },
        options?: { duration_seconds?: number },
    ): Observable<ConversationMessage> {
        const tempId = this.nextTempId();
        const optimistic = this.buildOptimisticMessage({
            tempId,
            conversationId,
            ctx,
            type: preview?.type || 'FILE',
            content: preview?.file_name || null,
            file_url: preview?.file_url || null,
            file_name: preview?.file_name || null,
            duration_seconds: options?.duration_seconds ?? null,
        });
        this.pendingPayloads.set(tempId, {
            kind: 'media',
            file,
            duration_seconds: options?.duration_seconds,
            preview,
        });
        this.pushOptimistic(optimistic);

        return this.postMedia(conversationId, file, tempId, options?.duration_seconds);
    }

    resend(tempId: string, ctx: SendMessageContext): Observable<ConversationMessage> {
        const payload = this.pendingPayloads.get(tempId);
        const msg = this._messages.value.find((m) => m.temp_id === tempId);
        if (!payload || !msg?.failed || !ctx) {
            return EMPTY;
        }

        this.markRetrying(tempId);

        if (payload.kind === 'text') {
            return this.postText(msg.conversation_id, payload.content, tempId);
        }

        return this.postMedia(
            msg.conversation_id,
            payload.file,
            tempId,
            payload.duration_seconds,
        );
    }

    markRead(conversationId: number): Observable<{ ok: boolean }> {
        return this.http.patch<{ ok: boolean }>(`${this.base}/${conversationId}/read`, {});
    }

    joinConversation(conversationId: number): void {
        if (this.joinedConversationId && this.joinedConversationId !== conversationId) {
            this.socket.emit('leaveConversation', this.joinedConversationId);
        }
        this.joinedConversationId = conversationId;
        this.currentConversationId = conversationId;
        this.socket.emit('joinConversation', conversationId);

        const cached = this.messageCache.get(conversationId);
        if (cached?.length) {
            this._messages.next(cached);
        }

        this.markRead(conversationId).subscribe();
    }

    leaveConversation(): void {
        if (this.joinedConversationId) {
            this.socket.emit('leaveConversation', this.joinedConversationId);
        }
        this.joinedConversationId = null;
        this.currentConversationId = null;
        this._messages.next([]);
    }

    clearMessages(): void {
        this._messages.next([]);
    }

    private postText(
        conversationId: number,
        content: string,
        tempId: string,
    ): Observable<ConversationMessage> {
        return this.http
            .post<ConversationMessage>(`${this.base}/${conversationId}/messages`, {
                content,
                type: 'TEXT',
            })
            .pipe(
                tap((msg) => this.confirmOptimistic(tempId, msg)),
                catchError((err) => {
                    this.failOptimistic(tempId);
                    return throwError(() => err);
                }),
            );
    }

    private postMedia(
        conversationId: number,
        file: File,
        tempId: string,
        durationSeconds?: number,
    ): Observable<ConversationMessage> {
        const formData = new FormData();
        formData.append('medias', file, file.name);
        if (durationSeconds != null) {
            formData.append('duration_seconds', String(durationSeconds));
        }

        return this.http.post<ConversationMessage>(`${this.base}/${conversationId}/messages`, formData).pipe(
            tap((msg) => this.confirmOptimistic(tempId, msg)),
            catchError((err) => {
                this.failOptimistic(tempId);
                return throwError(() => err);
            }),
        );
    }

    private nextTempId(): string {
        this.tempCounter += 1;
        return `opt-${Date.now()}-${this.tempCounter}`;
    }

    private buildOptimisticMessage(params: {
        tempId: string;
        conversationId: number;
        ctx: SendMessageContext;
        type: ConversationMessageType;
        content?: string | null;
        file_url?: string | null;
        file_name?: string | null;
        mime_type?: string | null;
        duration_seconds?: number | null;
    }): ConversationMessage {
        return {
            conversation_message_id: -this.tempCounter,
            conversation_id: params.conversationId,
            user_id: params.ctx.user_id,
            type: params.type,
            content: params.content ?? null,
            file_url: params.file_url ?? null,
            file_name: params.file_name ?? null,
            mime_type: params.mime_type ?? null,
            duration_seconds: params.duration_seconds ?? null,
            createdAt: new Date(),
            user: { user_id: params.ctx.user_id, name: params.ctx.user_name },
            optimistic: true,
            temp_id: params.tempId,
        };
    }

    private pushOptimistic(msg: ConversationMessage): void {
        if (this.currentConversationId !== msg.conversation_id) return;
        const next = [...this._messages.value, msg];
        this._messages.next(next);
        this.messageCache.set(msg.conversation_id, next);
        this.refreshConversationsPreview(msg);
    }

    private confirmOptimistic(tempId: string, real: ConversationMessage): void {
        this.pendingPayloads.delete(tempId);
        this.ingestServerMessage(real, tempId);
    }

    private failOptimistic(tempId: string): void {
        const current = this._messages.value;
        const next = current.map((m) =>
            m.temp_id === tempId ? { ...m, optimistic: false, failed: true } : m,
        );
        this._messages.next(next);
        if (this.currentConversationId) {
            this.messageCache.set(this.currentConversationId, next);
        }
    }

    private markRetrying(tempId: string): void {
        const current = this._messages.value;
        const next = current.map((m) =>
            m.temp_id === tempId ? { ...m, optimistic: true, failed: false } : m,
        );
        this._messages.next(next);
        if (this.currentConversationId) {
            this.messageCache.set(this.currentConversationId, next);
        }
    }

    private ingestServerMessage(real: ConversationMessage, replaceTempId?: string): void {
        if (!real?.conversation_message_id) return;

        const msgId = Number(real.conversation_message_id);
        if (msgId > 0) {
            if (this.recentlySeenMessageIds.has(msgId) && !replaceTempId) {
                return;
            }
            this.recentlySeenMessageIds.add(msgId);
            if (this.recentlySeenMessageIds.size > 200) {
                const first = this.recentlySeenMessageIds.values().next().value;
                if (first != null) this.recentlySeenMessageIds.delete(first);
            }
        }

        // Mensagem de outra conversa: só preview/unread/som — não misturar no thread aberto.
        if (
            this.currentConversationId != null &&
            real.conversation_id !== this.currentConversationId &&
            !replaceTempId
        ) {
            this.maybePlayIncomingMessageSound(real);
            this.refreshConversationsPreview(real);
            const cached = this.messageCache.get(real.conversation_id) || [];
            if (!cached.some((m) => m.conversation_message_id === real.conversation_message_id)) {
                this.messageCache.set(real.conversation_id, [...cached, real]);
            }
            return;
        }

        const current = this._messages.value;
        const replaceIdx = replaceTempId
            ? current.findIndex((m) => m.temp_id === replaceTempId)
            : current.findIndex(
                  (m) =>
                      m.optimistic &&
                      m.user_id === real.user_id &&
                      m.type === real.type &&
                      (m.type === 'TEXT'
                          ? m.content === real.content
                          : !!(m.file_name && m.file_name === real.file_name)),
              );

        let next: ConversationMessage[];
        if (replaceIdx >= 0) {
            next = [...current];
            next[replaceIdx] = real;
        } else if (!current.some((m) => m.conversation_message_id === real.conversation_message_id)) {
            next = [...current, real];
        } else {
            return;
        }

        // Corrige corrida HTTP+socket: mesma mensagem real pode entrar 2x.
        next = this.dedupeMessages(next);

        if (this.currentConversationId === real.conversation_id) {
            this._messages.next(next);
        }
        this.messageCache.set(real.conversation_id, next);

        if (this.currentConversationId === real.conversation_id) {
            this.markRead(real.conversation_id).subscribe();
        } else {
            this.maybePlayIncomingMessageSound(real);
        }
        this.refreshConversationsPreview(real);
    }

    private maybePlayIncomingMessageSound(msg: ConversationMessage): void {
        if (msg.optimistic) return;
        if (this.currentUserId != null && Number(msg.user_id) === Number(this.currentUserId)) return;
        const now = Date.now();
        if (now - this.lastChatNotifyAt < 300) return;
        this.lastChatNotifyAt = now;
        this.playIncomingMessageBeep();
    }

    private playIncomingMessageBeep(): void {
        try {
            const AudioContextCtor =
                window.AudioContext ||
                (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
            if (!AudioContextCtor) return;

            if (!this.chatNotifyAudioCtx || this.chatNotifyAudioCtx.state === 'closed') {
                this.chatNotifyAudioCtx = new AudioContextCtor();
            }
            const ctx = this.chatNotifyAudioCtx;
            if (ctx.state === 'suspended') {
                void ctx.resume();
            }

            const now = ctx.currentTime;
            const tones = [880, 1175];
            tones.forEach((freq, i) => {
                const t = now + i * 0.14;
                const oscillator = ctx.createOscillator();
                const gain = ctx.createGain();
                oscillator.type = 'sine';
                oscillator.frequency.value = freq;
                gain.gain.setValueAtTime(0.0001, t);
                gain.gain.exponentialRampToValueAtTime(0.18, t + 0.015);
                gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.11);
                oscillator.connect(gain);
                gain.connect(ctx.destination);
                oscillator.start(t);
                oscillator.stop(t + 0.13);
            });
        } catch {
            // Autoplay bloqueado ou Web Audio indisponível.
        }
    }

    private dedupeMessages(messages: ConversationMessage[]): ConversationMessage[] {
        const seenIds = new Set<number>();
        const seenTemps = new Set<string>();
        const out: ConversationMessage[] = [];
        for (const m of messages) {
            if (m.temp_id) {
                if (seenTemps.has(m.temp_id)) continue;
                seenTemps.add(m.temp_id);
            }
            if (m.conversation_message_id > 0) {
                if (seenIds.has(m.conversation_message_id)) continue;
                seenIds.add(m.conversation_message_id);
            }
            out.push(m);
        }
        return out;
    }

    private refreshConversationsPreview(msg: ConversationMessage): void {
        const list = this._conversations.value.map((c) => {
            if (c.conversation_id !== msg.conversation_id) return c;
            const unread =
                this.currentConversationId === msg.conversation_id && !msg.optimistic
                    ? 0
                    : msg.optimistic
                      ? c.unread_count || 0
                      : (c.unread_count || 0) + (this.currentConversationId === msg.conversation_id ? 0 : 1);
            return {
                ...c,
                last_message: msg.optimistic ? c.last_message : msg,
                last_message_at: msg.createdAt,
                unread_count: unread,
            };
        });
        this._conversations.next(list);
    }

    private mergeWithPending(
        serverMessages: ConversationMessage[],
        pending: ConversationMessage[],
    ): ConversationMessage[] {
        if (!pending.length) return serverMessages;
        const merged = [...serverMessages];
        for (const p of pending) {
            if (!merged.some((m) => m.temp_id === p.temp_id)) {
                merged.push(p);
            }
        }
        return merged.sort(
            (a, b) => new Date(a.createdAt as any).getTime() - new Date(b.createdAt as any).getTime(),
        );
    }

    private upsertConversation(conv: Conversation): void {
        if (!conv?.conversation_id) return;
        const list = this._conversations.value;
        const idx = list.findIndex((c) => c.conversation_id === conv.conversation_id);
        if (idx >= 0) {
            const next = [...list];
            next[idx] = { ...next[idx], ...conv };
            this._conversations.next(next);
        } else {
            this._conversations.next([conv, ...list]);
        }
    }
}
